import type { ComputedRef } from 'vue'
import type { AppTextColor } from '@shared/ui/typography'
import type { Subscription, SubscriptionRelationLink } from '@entities/subscription'

import { getCustomerUrl, getDisciplineUrl, getKidUrl } from '@shared/lib/routes'
import { SubscriptionModal } from '@entities/subscription'

import { useSubscriptionsStore } from './store'
import { useSubscriptionsApi } from '../api/list.hook'
import { useSubscriptionUpdate } from '../api/update.hook'
import {
  formatNextInvoice,
  getAutoRenewLabel,
  getAutoRenewTone,
  useSubscriptionOptions,
} from './subscription-options'
import { SUBSCRIPTION_EMPTY_CELL, isSubscriptionFailed } from './subscription-rules'

interface UseSubscriptionRowOptions {
  subscription: ComputedRef<Subscription>
}

interface SubscriptionRowReturn {
  isFailed: ComputedRef<boolean>
  isExpanded: ComputedRef<boolean>
  isProcessing: ComputedRef<boolean>
  customerName: ComputedRef<string | null>
  customerUrl: ComputedRef<string | null>
  kidLinks: ComputedRef<SubscriptionRelationLink[]>
  disciplineLinks: ComputedRef<SubscriptionRelationLink[]>
  autoRenewLabel: ComputedRef<string>
  autoRenewColor: ComputedRef<AppTextColor>
  subscriptionName: ComputedRef<string>
  paymentMethod: ComputedRef<string>
  nextInvoice: ComputedRef<string>
  emptyCell: string
  openDetails: () => void
  toggleExpanded: () => void
  changeDealOwner: (agentId: number | null) => Promise<void>
}

export const useSubscriptionRow = ({ subscription }: UseSubscriptionRowOptions): SubscriptionRowReturn => {
  const store = useSubscriptionsStore()
  const { getDisciplineLabel, getPaymentMethodLabel } = useSubscriptionOptions()

  const isFailed = computed<boolean>(() => isSubscriptionFailed(subscription.value))
  const isExpanded = computed<boolean>(() => store.getExpandedId === subscription.value.id)
  const isProcessing = computed<boolean>(() => store.isProcessing)

  const customerName = computed<string | null>(() => {
    const customer = subscription.value.customer

    return customer ? `${customer.first_name} ${customer.last_name}`.trim() : null
  })

  const customerUrl = computed<string | null>(() => {
    const customer = subscription.value.customer

    return customer ? getCustomerUrl(customer.id) : null
  })

  const kidLinks = computed<SubscriptionRelationLink[]>(() =>
    subscription.value.disciplines.map(({ id, kid }) => ({
      id,
      href: kid ? getKidUrl(kid.id) : null,
      text: kid?.name ?? SUBSCRIPTION_EMPTY_CELL,
      notes: kid?.notes ?? null,
    })))

  const disciplineLinks = computed<SubscriptionRelationLink[]>(() =>
    subscription.value.disciplines.map(({ id, code, notes }) => ({
      id,
      href: getDisciplineUrl(id),
      text: getDisciplineLabel(code),
      notes,
    })))

  const autoRenewLabel = computed<string>(() => getAutoRenewLabel(subscription.value.auto_renew))
  const autoRenewColor = computed<AppTextColor>(() => `${getAutoRenewTone(subscription.value.auto_renew)}-600`)

  const subscriptionName = computed<string>(() => subscription.value.name || SUBSCRIPTION_EMPTY_CELL)

  const paymentMethod = computed<string>(() => {
    const type = subscription.value.payment_method?.type

    return type ? getPaymentMethodLabel(type) : SUBSCRIPTION_EMPTY_CELL
  })

  const nextInvoice = computed<string>(() => formatNextInvoice(subscription.value))

  const openDetails = (): void => {
    store.openModal(SubscriptionModal.Details, subscription.value.id)
  }

  const toggleExpanded = (): void => {
    store.toggleExpanded(subscription.value.id)
  }

  const changeDealOwner = async (agentId: number | null): Promise<void> => {
    if (agentId === subscription.value.agent_id) return

    const updated = await useSubscriptionUpdate({
      subscriptionId: subscription.value.id,
      payload: { agent_id: agentId },
    })

    if (updated) {
      await useSubscriptionsApi()
    }
  }

  return {
    isFailed,
    isExpanded,
    isProcessing,
    customerName,
    customerUrl,
    kidLinks,
    disciplineLinks,
    autoRenewLabel,
    autoRenewColor,
    subscriptionName,
    paymentMethod,
    nextInvoice,
    emptyCell: SUBSCRIPTION_EMPTY_CELL,
    openDetails,
    toggleExpanded,
    changeDealOwner,
  }
}
